import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import "../styles/bookinghistory.css";
import bookingService from "../services/bookingService";
import guestHouseService from "../services/guestHouseService";
import BookingComponent from "../components/BookingComponent";
import * as XLSX from "xlsx";
import fileService from "../services/fileService";
import UploadSuccessModal from "../components/UploadSuccessModal";

const ViewBookingList = () => {
  // Function to get default date range (last month)
  const getDefaultDateRange = () => {
    const today = new Date();
    const lastMonth = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      today.getDate()
    );
    return {
      start: today.toISOString().split("T")[0],
      end: lastMonth.toISOString().split("T")[0],
    };
  };

  // document upload from grid var
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);

  const [isOpen, setisOpen] = useState(false);


  // States from original component
  const [bookings, setBookings] = useState([]);
  const [searchTerm, setSearchTerm] = useState(null);
  const [selectedGuestHouse, setSelectedGuestHouse] = useState("");
  // Add new state for booking status filter
  const [selectedBookingStatus, setSelectedBookingStatus] =
    useState("All Bookings");
  // Initialize with default date range
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [guestHouses, setGuestHouses] = useState([]);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(true);
  const [hideGuestHouseDropdown, setHideGuestHouseDropdown] = useState(false);
  const [showAddBookingButton, setShowAddBookingButton] = useState(false);
  const [showDownloadButton, setShowDownloadButton] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [cancellingBooking, setCancellingBooking] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [showDownloadFileOption, setShowDownloadFileOption] = useState(true);
  // Additional state for total amount calculation
  const [totalAmount, setTotalAmount] = useState(0);
  const [showTotalCard, setShowTotalCard] = useState(true);
  // Add state for tooltip
  const [tooltipVisible, setTooltipVisible] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // NEW: Add state variables for view and edit modals
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBookingForView, setSelectedBookingForView] = useState(null);
  const [selectedBookingForEdit, setSelectedBookingForEdit] = useState(null);

  // File upload state variables
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [uploadedFileId, setUploadedFileId] = useState(null);
  const [bookingFieldsEnabled, setBookingFieldsEnabled] = useState(false);
  const [bookingResponseData, setBookingResponseData] = useState(null);

  const [bookingSuccess, setBookingSuccess] = useState(false);

  // Fetch bookings with server-side filters (search term, guesthouse and date range)
  const fetchBookings = async () => {
    try {
      setLoading(true);

      // Create params object with all filters
      const params = {};

      // Add searchTerm to params if it exists
      if (searchTerm) {
        params.searchTerm = searchTerm;
      }

      // Only apply these filters if user is admin
      if (isAdmin) {
        if (selectedGuestHouse && !hideGuestHouseDropdown)
          params.guesthouseId =
            selectedGuestHouse === "0" ? "" : selectedGuestHouse;
        if (dateRange.start) params.startDate = dateRange.start;
        if (dateRange.end) params.endDate = dateRange.end;
      }

      // Call the API with parameters
      const data = await bookingService.getBookings(params);

      // Set the bookings directly from the API response
      setBookings(data);

      setError(null);
    } catch (err) {
      console.error("Error fetching bookings:", err);
      setError(
        err.message || "Failed to load bookings. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  // Function to filter bookings based on client-side criteria
  const getFilteredBookings = () => {
    let filteredBookings = [...bookings];

    // Filter by booking status when hideGuestHouseDropdown is true
    if (hideGuestHouseDropdown && selectedBookingStatus !== "All Bookings") {
      filteredBookings = filteredBookings.filter((booking) => {
        const bookingStatus = booking.BookingStatus || "";
        return (
          bookingStatus.toLowerCase() === selectedBookingStatus.toLowerCase()
        );
      });
    }

    return filteredBookings;
  };

  // Updated useEffect to call guest houses only once
  useEffect(() => {
    const initializeData = async () => {
      try {
        const user = localStorage.getItem("user")
          ? JSON.parse(localStorage.getItem("user"))
          : null;
        setCurrentUser(user);

        // Check URL and set admin status
        const currentUrl = window.location.pathname;
        let adminRole = false;

        // Check if user has admin role
        if (user && user.roles && user.roles.includes("admin")) {
          adminRole = true;
          setIsAdmin(true);
        }

        // Set UI states based on URL
        if (
          currentUrl.includes("viewbookings") ||
          currentUrl.includes("/reports/employee-wise")
        ) {
          setHideGuestHouseDropdown(true);
        }
        if (currentUrl.includes("viewbookings")) {
          if (adminRole) {
            setShowAddBookingButton(true);
          }
        }
        if (currentUrl.includes("reports")) {
          if (adminRole) {
            setShowDownloadButton(true);
          }
        }

        // Fetch guest houses only if admin and dropdown should be shown
        if (
          !currentUrl.includes("viewbookings") &&
          !currentUrl.includes("/reports/employee-wise")
        ) {
          try {
            const data = await guestHouseService.getGuestHouseList(false);
            setGuestHouses(data);
          } catch (err) {
            console.error("Error fetching guest houses:", err);
          }
        }
      } catch (error) {
        console.error("Error parsing user from localStorage:", error);
      }
    };

    // Initialize everything once on mount
    initializeData();
  }, []); // Empty dependency array - only run once on mount

  // Separate useEffect to handle initial booking fetch after initialization
  useEffect(() => {
    // Only fetch bookings once on mount
    fetchBookings();
  }, []); // Empty dependency array - only run once on mount

  // Calculate total amount whenever filtered bookings change
  useEffect(() => {
    calculateTotalAmount();
  }, [bookings, selectedBookingStatus]);

  // Function to calculate total amount from filtered bookings
  const calculateTotalAmount = () => {
    const filteredBookings = getFilteredBookings();
    let sum = 0;

    filteredBookings.forEach((booking) => {
      // Parse the FinalRoomRent as a number
      const amount = parseFloat(booking.FinalRoomRent) || 0;

      // Include amounts based on the selected filter
      if (hideGuestHouseDropdown && selectedBookingStatus !== "All Bookings") {
        // If a specific status is selected, include all amounts from that status
        sum += amount;
      } else {
        // For "All Bookings", only include amounts from non-cancelled bookings (original behavior)
        if (booking.BookingStatus !== "Cancelled") {
          sum += amount;
        }
      }
    });

    setTotalAmount(sum);
  };

  // Download bookings as XLS function with total amount
  const downloadBookingsXLS = () => {
    try {
      // Use filtered bookings for download
      const filteredBookings = getFilteredBookings();

      // Create the main data array
      const bookingsData = filteredBookings.map((booking, index) => ({
        "Serial No.": index + 1,
        "Booking ID": booking.CustomBookingID || "",
        "Guest Name": booking.GuestName,
        "CPF No.": booking.CPFNo,
        "Mobile No.": booking.MobileNo,
        "Email ID": booking.EmailID,
        "Guest Type": booking.GuestType || "",
        "Booking Type": booking.BookingType || "",
        "Travel Type": booking.TravelType || "",
        "Guest House Name": booking.GuestHouseName || booking.City,
        "Check-In": new Date(booking.CheckInDate),
        "Check-Out": new Date(booking.CheckOutDate),
        "No. of Days": booking.TotalTimeStay,
        Status: booking.BookingStatus,
        Price: booking.FinalRoomRent,
      }));

      // Add an empty row for separation
      bookingsData.push({
        "Serial No.": "",
        "Booking ID": "",
        "Guest Name": "",
        "CPF No.": "",
        "Mobile No.": "",
        "Email ID": "",
        "Guest Type": "",
        "Booking Type": "",
        "Travel Type": "",
        "Guest House Name": "",
        "Check-In": "",
        "Check-Out": "",
        "No. of Days": "",
        Price: "",
        Status: "",
      });

      // Add total row
      bookingsData.push({
        "Serial No.": "",
        "Booking ID": "",
        "Guest Name": "",
        "CPF No.": "",
        "Mobile No.": "",
        "Email ID": "",
        "Guest Type": "",
        "Booking Type": "",
        "Travel Type": "",
        "Guest House Name": "",
        "Check-In": "",
        "Check-Out": "",
        "No. of Days": "",
        Status: "TOTAL:",
        Price: totalAmount,
      });

      // Create a new worksheet
      const worksheet = XLSX.utils.json_to_sheet(bookingsData);

      // Optional: Style the total row (if you want to make it bold)
      // Note: Basic styling may not work in all Excel versions, but you can try
      const totalRowIndex = bookingsData.length + 1; // +1 because Excel rows are 1-indexed and include header

      // Create a new workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Bookings");

      // Generate filename with current date
      const date = new Date();
      const formattedDate = date.toISOString().split("T")[0]; // YYYY-MM-DD format
      const fileName = `ONGC_Bookings_${formattedDate}.xlsx`;

      // Write the workbook and download
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error("Error downloading XLS:", error);
      alert("Failed to download bookings data. Please try again.");
    }
  };

  // NEW: Function to open view booking modal
  const openViewBooking = (booking) => {
    setSelectedBookingForView(booking);
    setShowViewModal(true);
  };

  // NEW: Function to close view booking modal
  const closeViewBooking = () => {
    setSelectedBookingForView(null);
    setShowViewModal(false);
  };

  // NEW: Function to open edit booking modal
  const openEditBooking = (booking) => {
    setSelectedBookingForEdit(booking);
    setShowEditModal(true);
  };

  // NEW: Function to close edit booking modal
  const closeEditBooking = () => {
    setSelectedBookingForEdit(null);
    setShowEditModal(false);
    // Refresh bookings after editing
    fetchBookings();
  };

  // NEW: Check if a booking can be edited
  const canEditBooking = (booking) => {
    return (
      booking &&
      booking.BookingStatus !== "Cancelled" &&
      booking.BookingStatus !== "Completed"
    );
  };

  // Function to open cancel confirmation popup
  const openCancelConfirmation = (booking) => {
    setBookingToCancel(booking);
    setCancelReason(""); // Clear previous reason
    setShowCancelConfirmation(true);
  };

  // Function to close cancel confirmation popup
  const closeCancelConfirmation = () => {
    setBookingToCancel(null);
    setShowCancelConfirmation(false);
    setCancelReason(""); // Reset to default reason
  };

  // Function to handle cancel booking
  const handleCancelBooking = async () => {
    if (!bookingToCancel) return;

    try {
      setCancellingBooking(true);

      // Call the API to cancel the booking
      await bookingService.cancelBooking(
        bookingToCancel.BookingID,
        cancelReason
      );

      // Refresh bookings list after cancellation
      fetchBookings();

      // Show success message
      alert("Booking cancelled successfully");

      // Close the confirmation popup
      closeCancelConfirmation();
    } catch (error) {
      console.error("Error cancelling booking:", error);
      alert(error.message || "Failed to cancel booking. Please try again.");
    } finally {
      setCancellingBooking(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const getReportType = () => {
    const currentUrl = window.location.pathname;

    if (currentUrl.includes("/reports/employee-wise")) {
      return "Employee Wise";
    } else if (currentUrl.includes("/reports/property-wise")) {
      return "Property Wise";
    } else {
      return "All Bookings";
    }
  };

  // Update the search debounce useEffect
  useEffect(() => {
    if (searchTerm === null) return; // Skip if searchTerm is null
    // Create a timer to delay the API call
    const delayDebounceFn = setTimeout(() => {
      // Call API for both empty and non-empty search terms
      fetchBookings();
    }, 500); // 500ms delay

    // Clear the timeout if searchTerm changes before the delay expires
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  // Add this function to your component
  const clearSearch = () => {
    setSearchTerm("");
    // API will be called by useEffect with empty search term
  };

  // Handle guesthouse dropdown change
  const handleGuestHouseChange = (e) => {
    const newValue = e.target.value;
    console.log("Selected Guest House:", newValue);
    setSelectedGuestHouse(newValue);
  };

  // Handle booking status change (client-side filtering)
  const handleBookingStatusChange = (e) => {
    const newValue = e.target.value;
    console.log("Selected Booking Status:", newValue);
    setSelectedBookingStatus(newValue);
    // No API call needed - this will trigger re-render with filtered data
  };

  useEffect(() => {
    if (isAdmin) {
      if (selectedGuestHouse) {
        fetchBookings();
      }
    }
  }, [selectedGuestHouse]);

  // Handle date range input changes - removed auto-fetch
  const handleDateRangeChange = (e, type) => {
    setDateRange((prev) => ({
      ...prev,
      [type]: e.target.value,
    }));
  };

  // Clear date range
  const clearDateRange = () => {
    setDateRange({ start: "", end: "" });

    // Call API when date range is cleared
    setTimeout(() => {
      fetchBookings();
    }, 0);
  };

  // Toggle date picker dropdown
  const toggleDatePicker = () => {
    setIsDatePickerOpen(!isDatePickerOpen);
  };

  // Apply date filter when apply button is clicked - only API call here
  const applyDateFilter = () => {
    fetchBookings(); // Call API with current date range
    setIsDatePickerOpen(false); // Close the date picker
  };

  // Format date from ISO to readable format
  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    const options = { day: "numeric", month: "long", year: "numeric" };
    return date.toLocaleDateString("en-US", options);
  };

  // Format date for display in the date range button
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const options = { day: "numeric", month: "short", year: "numeric" };
    return date.toLocaleDateString("en-US", options);
  };

  // Get display text for the date range button
  const getDateRangeDisplayText = () => {
    if (dateRange.start && dateRange.end) {
      return `${formatDateForDisplay(dateRange.start)} - ${formatDateForDisplay(
        dateRange.end
      )}`;
    } else if (dateRange.start) {
      return `From ${formatDateForDisplay(dateRange.start)}`;
    } else if (dateRange.end) {
      return `Until ${formatDateForDisplay(dateRange.end)}`;
    }
    return "Select Date Range";
  };

  // Handle opening the booking modal
  const openBookingModal = () => {
    setIsBookingModalOpen(true);
  };

  // Handle closing the booking modal
  const closeBookingModal = () => {
    setIsBookingModalOpen(false);
    fetchBookings(); // Refresh bookings after closing
  };

  // Function to determine status class based on booking status
  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "status-active";
      case "confirmed":
        return "badge colorsuccess";
      case "pending":
        return "status-pending";
      case "cancelled":
        return "badge bg-danger";
      case "completed":
        return "status-completed";
      default:
        return "status-pending"; // Default status
    }
  };

  // Check if a booking can be cancelled
  const canCancelBooking = (booking) => {
    return booking && booking.BookingStatus !== "Cancelled";
  };

  // Toggle the total amount card
  const toggleTotalCard = () => {
    setShowTotalCard(!showTotalCard);
  };

  // Get the label for total amount based on selected filter
  const getTotalAmountLabel = () => {
    if (hideGuestHouseDropdown && selectedBookingStatus !== "All Bookings") {
      return `Total Amount (${selectedBookingStatus})`;
    }
    return "Total Amount (Active Bookings)";
  };

  // Tooltip handlers
  const handleInfoIconMouseEnter = (e, bookingId, cancelReason) => {
    if (cancelReason) {
      const rect = e.target.getBoundingClientRect();
      setTooltipPosition({
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
      });
      setTooltipVisible({ bookingId, reason: cancelReason });
    }
  };

  const handleInfoIconMouseLeave = () => {
    setTooltipVisible(null);
  };

  const handleUploadClick = (e,booking) => {
    document.getElementById("fileupload"+booking.BookingID).click();
  }
  // Get filtered bookings for display
  const displayBookings = getFilteredBookings();

  const getFileExtension = (filename) => {
    return filename.slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2);
  };

  const createRenamedFile = (file, bookingId) => {
    const fileExtension = getFileExtension(file.name);
    const newFileName = `${bookingId}.${fileExtension}`;

    const renamedFile = new File([file], newFileName, {
      type: file.type,
      lastModified: file.lastModified,
    });

    return renamedFile;
  };

  //upload document function from grid
  const handleUpload =  async (event,booking) => { 
    const file = event.target.files[0];
    if (!file) return alert("Please select a file first!");
    const bookingId = booking.BookingID;
    const renamedFile = createRenamedFile(file, bookingId);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("bookingId", bookingId);
    formData.append("guestName", booking.GuestName);
    formData.append("emailId", booking.EmailID);
    formData.append("fileType", renamedFile.type);
    formData.append("description", `Document for booking ${bookingId}`);
    
    for (let [key, value] of formData.entries()) {
      console.log(key, value);
    }

    try {
        const fileResponse = await fileService.uploadFile(formData);
        console.log("File uploaded successfully:", fileResponse);
        setisOpen(true);
        setBookingSuccess(true);
        // if (fileResponse?.filePath) 
        // {
        //   setUploadedFileUrl(fileResponse.filePath);
        //   setUploadedFileId(fileResponse.fileId);
        //   setUploadedFileName(renamedFile.name);
        // }
    } catch (err) {
      console.error(err);
      alert(err);
    }
  };

  const resetForm = () => {
    setBookingSuccess(false);
  };

  return (
    <div className="main-container">
      <Navbar />
      <div style={{ height: "15px" }}></div>

      <main className="container-fluid">
        <div className="bookings-header container-fluid">
          {/* <h1 className="bookings-title">ONGC Guest House Bookings</h1> */}

          {/* Single Controls Row for All Elements */}
          <div className="controls-row">
            <div className="controls-container">
              {/* Filters Section */}
              <div className="filters-section">
                <div style={{ fontSize: "20px", color: "#514e4e" }}>
                  {getReportType()}
                </div>
              </div>

              {/* Action Buttons Section */}
              <div className="action-buttons">
                <div className="search-box">
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search by Name, CPF No. or mobile..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                  {searchTerm && (
                    <button
                      className="search-clear-btn"
                      onClick={clearSearch}
                      title="Clear search"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                      >
                        <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                      </svg>
                    </button>
                  )}
                  <div className="search-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
                    </svg>
                  </div>
                </div>
                {isAdmin && !hideGuestHouseDropdown && (
                  <div className="guesthouse-filter">
                    <select
                      value={selectedGuestHouse}
                      onChange={handleGuestHouseChange}
                      className="guesthouse-select"
                    >
                      <option value="0">All Guest Houses</option>
                      {guestHouses.map((guesthouse) => (
                        <option key={guesthouse.id} value={guesthouse.id}>
                          {guesthouse.guestHouseName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {hideGuestHouseDropdown && (
                  <div className="guesthouse-filter">
                    <select
                      value={selectedBookingStatus}
                      onChange={handleBookingStatusChange}
                      className="guesthouse-select"
                    >
                      <option value="All Bookings">All Bookings</option>
                      <option value="Confirmed">Confirmed</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>
                )}
                {isAdmin && (
                  <div className="date-range-filter">
                    <button
                      className="date-picker-button"
                      onClick={toggleDatePicker}
                    >
                      <span className="calendar-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z" />
                        </svg>
                      </span>
                      <span className="date-text">
                        {getDateRangeDisplayText()}
                      </span>
                    </button>

                    {isDatePickerOpen && (
                      <div className="date-picker-dropdown">
                        <div className="date-inputs">
                          <div className="date-input-group">
                            <label>Start Date</label>
                            <input
                              type="date"
                              value={dateRange.start}
                              onChange={(e) =>
                                handleDateRangeChange(e, "start")
                              }
                              className="date-input"
                            />
                          </div>
                          <div className="date-input-group">
                            <label>End Date</label>
                            <input
                              type="date"
                              value={dateRange.end}
                              onChange={(e) => handleDateRangeChange(e, "end")}
                              className="date-input"
                            />
                          </div>
                        </div>
                        <div className="date-picker-footer">
                          <button
                            className="date-apply-btn"
                            onClick={applyDateFilter}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {showAddBookingButton && (
                  <button
                    className="btn btn-primary"
                    style={{ fontSize: "14px" }}
                    onClick={openBookingModal}
                  >
                    Add Booking
                  </button>
                )}
                {showDownloadButton && (
                  <button
                    className="btn btn-primary"
                    onClick={downloadBookingsXLS}
                    disabled={loading || displayBookings.length === 0}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                      style={{ marginRight: "5px" }}
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path d="M8 4a.5.5 0 0 1 .5.5v5.793l2.146-2.147a.5.5 0 0 1 .708.708l-3 3a.5.5 0 0 1-.708 0l-3-3a.5.5 0 1 1 .708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                    </svg>
                    Download Excel
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="bookings-container container-fluid">
          <div className="bookings-wrapper">
            <div className="card shadow">
              <div className="card-body" style={{ padding: "0px" }}>
                <div className="table-responsive">
                  {loading ? (
                    <div className="loading-container">
                      <div className="spinner"></div>
                      <p>Loading bookings...</p>
                    </div>
                  ) : error ? (
                    <div className="error-container">
                      <p>{error}</p>
                    </div>
                  ) : (
                    <>
                      <table className="bookings-table">
                        <thead>
                          <tr>
                            <th style={{ paddingLeft: "20px" }}>Serial No.</th>
                            <th>Booking ID</th>
                            <th>Guest Name</th>
                            <th>CPF No.</th>
                            <th>Mobile No.</th>
                            <th>Email ID</th>
                            <th>Guest Type</th>
                            <th>Booking Type</th>
                            <th>Travel Type</th>
                            <th>Guest House Name</th>
                            <th>Check-In Date</th>
                            <th>Check-Out Date</th>
                            <th>No. of Days</th>
                            {!showDownloadButton && <th>Rooms</th>}
                            {!showDownloadButton && <th>No. of Adults</th>}
                            {!showDownloadButton && <th>No. of Children</th>}
                            <th>Price</th>
                            {!showDownloadButton && <th>Status</th>}
                            {!showDownloadButton && <th>ID</th>}
                            {!showDownloadButton && <th>Action</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {displayBookings.length > 0 ? (
                            displayBookings.map((booking, index) => (
                              <tr key={booking.BookingID}>
                                <td style={{ paddingLeft: "20px" }}>
                                  {index + 1}
                                </td>
                                <td>
                                  {booking.CustomBookingID || booking.BookingID}
                                </td>
                                <td>{booking.GuestName}</td>
                                <td>{booking.CPFNo || "-"}</td>
                                <td>{booking.MobileNo}</td>
                                <td>{booking.EmailID}</td>
                                <td>{booking.GuestType || "-"}</td>
                                <td>{booking.BookingType || "-"}</td>
                                <td>{booking.TravelType || "-"}</td>
                                <td>{booking.GuestHouseName}</td>
                                <td>{formatDate(booking.CheckInDate)}</td>
                                <td>{formatDate(booking.CheckOutDate)}</td>
                                <td>{booking.TotalTimeStay}</td>
                                {!showDownloadButton && (
                                  <td>
                                    {" "}
                                    {parseInt(booking.NoOfRooms) +
                                      parseInt(booking.NoOfSuitesRooms)}
                                  </td>
                                )}
                                {!showDownloadButton && (
                                  <td>{booking.NoOfAdults}</td>
                                )}
                                {!showDownloadButton && (
                                  <td>{booking.NoOfChild}</td>
                                )}
                                <td>
                                  Rs. {!canCancelBooking(booking) ? "-" : ""}{" "}
                                  {booking.FinalRoomRent}
                                </td>
                                {!showDownloadButton && (
                                  <td>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "5px",
                                      }}
                                    >
                                      <span
                                        style={{ padding: "8px" }}
                                        className={`booking-status ${getStatusClass(
                                          booking.BookingStatus
                                        )}`}
                                      >
                                        {booking.BookingStatus}
                                      </span>
                                      {booking.IsCancelled == 1 &&
                                        booking.CancelReason && (
                                          <div
                                            className="relative inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 hover:bg-blue-200 cursor-pointer transition-all duration-200"
                                            onMouseEnter={(e) =>
                                              handleInfoIconMouseEnter(
                                                e,
                                                booking.BookingID,
                                                booking.CancelReason
                                              )
                                            }
                                            onMouseLeave={
                                              handleInfoIconMouseLeave
                                            }
                                            title="View cancellation reason"
                                          >
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              width="12"
                                              height="12"
                                              fill="#3b82f6"
                                              viewBox="0 0 16 16"
                                              className="drop-shadow-sm"
                                            >
                                              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                              <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                                            </svg>
                                          </div>
                                        )}
                                    </div>
                                  </td>
                                )}
                                {!showDownloadButton && (
                                  <td>
                                    {booking.FileID ? (
                                      <div className="file-download">
                                        <a
                                          href={fileService.getFileDownloadUrl(
                                            booking.FileID
                                          )}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="download-link"
                                        >
                                          <i
                                            className="fas fa-download"
                                            style={{ color: "#000" }}
                                          ></i>
                                        </a>
                                      </div>
                                    ) : (
                                      <div>
                                        <input id={"fileupload"+booking.BookingID} type="file" accept="image/*" onChange={(e) => handleUpload(e,booking)} style={{ display: "none" }}/>
                                        <span class='action-btn edit-btn'
                                        onClick={(e) => handleUploadClick(e,booking)}
                                        >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="black">
                                        <rect x="8" y="40" width="48" height="12" rx="6" ry="6" />
                                        <circle cx="48" cy="46" r="4" fill="black" />
                                        <path d="M32 8 L48 28 H38 V40 H26 V28 H16 Z" />
                                        </svg>
                                        </span>
                                      </div>
                                    )}
                                  </td>
                                )}
                                {/* UPDATED: Replace the existing Action column with new View, Edit, Cancel buttons */}
                                {!showDownloadButton && (
                                  <td>
                                    <div className="action-buttons-group">
                                      
                                  
                                      {/* Edit Button */}
                                      <span
                                        className={`action-btn edit-btn ${
                                          !canEditBooking(booking)
                                            ? "disabled-action"
                                            : ""
                                        }`}
                                        title={
                                          canEditBooking(booking)
                                            ? "Edit Booking"
                                            : "Booking cannot be edited"
                                        }
                                        onClick={() =>
                                          canEditBooking(booking) &&
                                          openEditBooking(booking)
                                        }
                                      >
                                        <i class="far fa-edit"></i>
                                      </span>

                                      {/* <i class="fas fa-pen"></i>
                                        <i class="fas fa-pencil"></i> 
                                        <i class="fas fa-edit"></i> 
                                        <i class="fas fa-pencil-alt"></i>``
                                      */}

                                      {/* Cancel Button */}
                                      <span
                                        className={`action-btn cancel-btn ${
                                          !canCancelBooking(booking)
                                            ? "disabled-action"
                                            : ""
                                        }`}
                                        title={
                                          canCancelBooking(booking)
                                            ? "Cancel Booking"
                                            : "Booking already cancelled"
                                        }
                                        onClick={() =>
                                          canCancelBooking(booking) &&
                                          openCancelConfirmation(booking)
                                        }
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          fill="currentColor"
                                          viewBox="0 0 16 16"
                                        >
                                          <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                                          <path
                                            fillRule="evenodd"
                                            d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"
                                          />
                                        </svg>
                                      </span>
                                    </div>
                                  </td>
                                )}
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="18" className="empty-message">
                                No bookings found matching your search.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>

                {/* Total Amount Summary Panel - Inside card */}
                {displayBookings.length > 0 && (
                  <div className="total-summary-panel mt-3 border-top pt-3">
                    <div className="total-summary-content">
                      <div className="summary-item total">
                        <span className="summary-label">
                          {getTotalAmountLabel()}
                        </span>
                        <span className="summary-value">
                          Rs. {totalAmount.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Hover Tooltip for Full Reason */}
      {tooltipVisible && (
        <div
          style={{
            position: "fixed",
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: "translateX(-50%) translateY(-100%)",
            backgroundColor: "#333",
            color: "white",
            padding: "10px 15px",
            borderRadius: "6px",
            fontSize: "13px",
            maxWidth: "350px",
            wordWrap: "break-word",
            zIndex: 1001,
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            pointerEvents: "none",
            lineHeight: "1.4",
          }}
        >
          <div>{tooltipVisible.reason}</div>
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "8px solid transparent",
              borderRight: "8px solid transparent",
              borderTop: "8px solid #333",
            }}
          />
        </div>
      )}

      {/* Booking Modal */}
      <BookingComponent
        isOpen={isBookingModalOpen}
        onClose={closeBookingModal}
      />

      {/* NEW: View Booking Modal */}
      {showViewModal && selectedBookingForView && (
        <div className="modal-overlay">
          <div
            className="modal-container view-modal"
            style={{ maxWidth: "100%" }}
          >
            <div className="modal-header">
              <h2>Booking Details</h2>
              <button className="close-button" onClick={closeViewBooking}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="booking-details-grid">
                <div className="detail-section">
                  <h4>Guest Information</h4>
                  <div className="detail-item">
                    <strong>Booking ID:</strong>{" "}
                    {selectedBookingForView.CustomBookingID ||
                      selectedBookingForView.BookingID}
                  </div>
                  <div className="detail-item">
                    <strong>Guest Name:</strong>{" "}
                    {selectedBookingForView.GuestName}
                  </div>
                  <div className="detail-item">
                    <strong>CPF No.:</strong>{" "}
                    {selectedBookingForView.CPFNo || "-"}
                  </div>
                  <div className="detail-item">
                    <strong>Mobile No.:</strong>{" "}
                    {selectedBookingForView.MobileNo}
                  </div>
                  <div className="detail-item">
                    <strong>Email ID:</strong> {selectedBookingForView.EmailID}
                  </div>
                  <div className="detail-item">
                    <strong>Guest Type:</strong>{" "}
                    {selectedBookingForView.GuestType || "-"}
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Booking Information</h4>
                  <div className="detail-item">
                    <strong>Booking Type:</strong>{" "}
                    {selectedBookingForView.BookingType || "-"}
                  </div>
                  <div className="detail-item">
                    <strong>Travel Type:</strong>{" "}
                    {selectedBookingForView.TravelType || "-"}
                  </div>
                  <div className="detail-item">
                    <strong>Check-In Date:</strong>{" "}
                    {formatDate(selectedBookingForView.CheckInDate)}
                  </div>
                  <div className="detail-item">
                    <strong>Check-Out Date:</strong>{" "}
                    {formatDate(selectedBookingForView.CheckOutDate)}
                  </div>
                  <div className="detail-item">
                    <strong>No. of Days:</strong>{" "}
                    {selectedBookingForView.TotalTimeStay}
                  </div>
                  <div className="detail-item">
                    <strong>Status:</strong>
                    <span
                      className={`booking-status ${getStatusClass(
                        selectedBookingForView.BookingStatus
                      )}`}
                      style={{ marginLeft: "8px" }}
                    >
                      {selectedBookingForView.BookingStatus}
                    </span>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Accommodation Details</h4>
                  <div className="detail-item">
                    <strong>Guest House:</strong>{" "}
                    {selectedBookingForView.GuestHouseName}
                  </div>
                  <div className="detail-item">
                    <strong>No. of Rooms:</strong>{" "}
                    {parseInt(selectedBookingForView.NoOfRooms || 0) +
                      parseInt(selectedBookingForView.NoOfSuitesRooms || 0)}
                  </div>
                  <div className="detail-item">
                    <strong>No. of Adults:</strong>{" "}
                    {selectedBookingForView.NoOfAdults}
                  </div>
                  <div className="detail-item">
                    <strong>No. of Children:</strong>{" "}
                    {selectedBookingForView.NoOfChild}
                  </div>
                  <div className="detail-item">
                    <strong>Final Amount:</strong> Rs.{" "}
                    {selectedBookingForView.FinalRoomRent}
                  </div>
                  {selectedBookingForView.CancelReason && (
                    <div className="detail-item">
                      <strong>Cancel Reason:</strong>{" "}
                      {selectedBookingForView.CancelReason}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    {/* DOCUMENT UPLOAD */}
    {bookingSuccess && (
      <>
      <UploadSuccessModal
        isOpen={bookingSuccess}
        onClose={() => setBookingSuccess(false)}
        fetchBookings={fetchBookings}
      />
      </>
    )}

      {/* NEW: Edit Booking Modal */}
      {showEditModal && selectedBookingForEdit && (
        <>
          <BookingComponent
            isOpen={showEditModal}
            onClose={closeEditBooking}
            editMode={true}
            bookingData={selectedBookingForEdit}
          />
        </>
        // <div className="modal-overlay">
        //   <div className="modal-container edit-modal">
        //     <div className="modal-header">
        //       <h2>Edit Booking</h2>
        //       <button className="close-button" onClick={closeEditBooking}>
        //         &times;
        //       </button>
        //     </div>
        //     <div className="modal-body">
        //       {/* You can integrate your existing BookingComponent here for editing */}
        //       {/* Or create a separate EditBookingComponent */}
        //       <BookingComponent
        //         isOpen={showEditModal}
        //         onClose={closeEditBooking}
        //         editMode={true}
        //         bookingData={selectedBookingForEdit}
        //       />
        //     </div>
        //   </div>
        // </div>
      )}

      {/* Cancel Booking Confirmation Modal */}
      {showCancelConfirmation && (
        <div className="modal-overlay">
          <div
            className="modal-container cancel-modal"
            style={{ width: "40%" }}
          >
            <div className="modal-header">
              <h2>Cancel Booking</h2>
              <button
                className="close-button"
                onClick={closeCancelConfirmation}
                disabled={cancellingBooking}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to cancel this booking?</p>

              <div style={{ marginTop: "15px" }}>
                <textarea
                  id="cancelReason"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Please provide a reason for cancellation..."
                  rows={4}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "14px",
                    resize: "vertical",
                    minHeight: "80px",
                  }}
                  disabled={cancellingBooking}
                  required
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="confirm-button"
                onClick={handleCancelBooking}
                disabled={cancellingBooking || !cancelReason.trim()}
                style={{
                  opacity: !cancelReason.trim() || cancellingBooking ? 0.6 : 1,
                  cursor:
                    !cancelReason.trim() || cancellingBooking
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                {cancellingBooking ? "Cancelling..." : "Yes, Cancel Booking"}
              </button>
              <button
                className="cancel-button"
                onClick={closeCancelConfirmation}
                disabled={cancellingBooking}
              >
                No, Keep Booking
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ViewBookingList;
